import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Alert,
  Button,
  Card,
  Col,
  Collapse,
  Descriptions,
  Empty,
  List,
  Progress,
  Row,
  Space,
  Spin,
  Tag,
  Typography,
  message,
} from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { useHistory, useLocation } from 'umi';
import { getMemberTrainingWorkspaceCourseWorkspaceDetail } from '@/services/technical';
import CourseStructure from './CourseStructure';
import styles from './index.less';
import {
  loadActionNames,
  RECORD_STATUS_LABELS,
  parseJson,
  success,
  timeText,
} from '../presentation';

const { Paragraph, Text, Title } = Typography;
const { Panel } = Collapse;

const ASSESSMENT_STATUS = {
  stable: { label: '表现稳定', color: 'green' },
  acceptable: { label: '基本达到', color: 'blue' },
  needs_attention: { label: '需要关注', color: 'orange' },
  insufficient_evidence: { label: '数据不足', color: 'default' },
};

const DIMENSION_NAMES = {
  movement_quality: '动作质量',
  stability_control: '稳定与控制',
  load_response: '负荷与身体反应',
  coaching_response: '指导响应',
};

const CONFIDENCE_NAMES = {
  high: '证据充分',
  medium: '证据一般',
  low: '证据有限',
};

const RECORD_STATUS_COLORS = {
  generating: 'blue',
  succeeded: 'green',
  failed: 'red',
  not_generated: 'default',
};

function assessmentTag(status) {
  const config =
    ASSESSMENT_STATUS[status] || ASSESSMENT_STATUS.insufficient_evidence;
  return <Tag color={config.color}>{config.label}</Tag>;
}

function AssessmentSection({ title, section }) {
  return (
    <Card
      size="small"
      title={title}
      bordered={false}
      className={styles.assessmentSection}
      style={{ height: '100%' }}
    >
      <Space direction="vertical" size={8}>
        {assessmentTag(section?.status)}
        <Paragraph
          ellipsis={{ rows: 3, expandable: true, symbol: '展开' }}
          className={styles.assessmentText}
        >
          {section?.summary || '暂时没有足够数据形成判断'}
        </Paragraph>
      </Space>
    </Card>
  );
}

AssessmentSection.propTypes = {
  title: PropTypes.string.isRequired,
  section: PropTypes.shape({
    status: PropTypes.string,
    summary: PropTypes.string,
  }),
};

AssessmentSection.defaultProps = {
  section: null,
};

function durationText(startAt, endAt) {
  if (!startAt || !endAt || endAt < startAt) return '-';
  const minutes = Math.max(1, Math.round((endAt - startAt) / 60000));
  return `${minutes} 分钟`;
}

function metricNumber(items, metricCode) {
  const item = (items || []).find(
    (candidate) => candidate?.metricCode === metricCode,
  );
  const value = Number(item?.value);
  return Number.isFinite(value) ? value : null;
}

export default function MemberTrainingCourseDetail() {
  const history = useHistory();
  const location = useLocation();
  const { memberId, roomId } = location.query || {};
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);
  const [actionNames, setActionNames] = useState({});

  const evaluation = detail?.evaluation;
  const observation = useMemo(
    () => parseJson(evaluation?.observationContent),
    [evaluation],
  );
  const assessment = useMemo(
    () => parseJson(evaluation?.assessmentContent),
    [evaluation],
  );
  const courseSummary = (observation?.observations || []).filter(
    (item) => item.scope === 'course_summary',
  );
  const actionCatalogById = useMemo(
    () =>
      (observation?.actionCatalog || []).reduce((result, item) => {
        if (item?.actionId) result[item.actionId] = item;
        return result;
      }, {}),
    [observation],
  );
  const actionInstances = useMemo(() => {
    const grouped = new Map();
    (observation?.actionInstances || []).forEach((instance) => {
      // 后端的 instanceId/snapshotId 才是动作实例身份；相同动作可能在
      // 同一阶段重复编排，不能仅按动作和序列字段合并后少算完成率。
      const key =
        instance.instanceId ||
        (instance.snapshotId !== null && instance.snapshotId !== undefined
          ? `snapshot:${instance.snapshotId}`
          : `execution:${instance.phaseId || 0}:${instance.actionId || 0}:${
              instance.actionSeqId || 0
            }`);
      grouped.set(key, { ...(grouped.get(key) || {}), ...instance });
    });
    return [...grouped.values()].map((instance) => ({
      ...instance,
      definition: actionCatalogById[instance.actionId] || null,
      observations: (observation?.observations || []).filter(
        (item) =>
          (instance.evaluationId &&
            item.evaluationId === instance.evaluationId) ||
          (item.actionId === instance.actionId &&
            item.actionSeqId === instance.actionSeqId &&
            (!item.phaseId || item.phaseId === instance.phaseId)),
      ),
    }));
  }, [actionCatalogById, observation]);
  const availability = observation?.availability || {};

  useEffect(() => {
    let active = true;
    async function load() {
      if (!memberId || !roomId) {
        message.error('缺少会员或课程信息');
        setLoading(false);
        return;
      }
      try {
        const response = await getMemberTrainingWorkspaceCourseWorkspaceDetail({
          requestData: {
            memberId: Number(memberId),
            roomId: Number(roomId),
          },
        });
        if (!success(response)) {
          message.error(response?.msg || '课程评估详情查询失败');
          return;
        }
        if (active) setDetail(response.data || null);
      } catch (error) {
        message.error('课程评估详情暂时不可用');
      } finally {
        if (active) setLoading(false);
      }
    }
    load();
    return () => {
      active = false;
    };
  }, [memberId, roomId]);

  useEffect(() => {
    let active = true;
    const actionIds = [
      ...new Set(actionInstances.map((item) => item.actionId)),
    ].filter(
      (actionId) => actionId && !actionCatalogById[actionId]?.actionName,
    );
    if (!actionIds.length) {
      setActionNames({});
      return undefined;
    }
    loadActionNames(actionIds).then((names) => {
      if (active) setActionNames(names);
    });
    return () => {
      active = false;
    };
  }, [actionCatalogById, actionInstances]);

  const openActionDetail = (instance) => {
    history.push({
      pathname: '/technical/memberTrainingWorkspace',
      query: {
        view: 'actionDetail',
        memberId,
        evaluationId: instance.evaluationId,
      },
    });
  };

  if (loading) {
    return (
      <Card style={{ textAlign: 'center', padding: 64 }}>
        <Spin tip="正在加载课程评估" />
      </Card>
    );
  }

  const classroom = detail?.classroom;
  const hasActionInstances = actionInstances.length > 0;
  const plannedActionCount = hasActionInstances
    ? actionInstances.length
    : metricNumber(courseSummary, 'course.plannedActionCount') || 0;
  const completedActionCount = hasActionInstances
    ? actionInstances.filter((item) => item.completionStatus === 'completed')
        .length
    : metricNumber(courseSummary, 'course.completedActionCount') || 0;
  const skippedActionCount = hasActionInstances
    ? actionInstances.filter((item) => item.completionStatus === 'skipped')
        .length
    : metricNumber(courseSummary, 'course.skippedActionCount') || 0;
  const incompleteActionCount = hasActionInstances
    ? actionInstances.filter(
        (item) =>
          item.completionStatus !== 'completed' &&
          item.completionStatus !== 'skipped',
      ).length
    : metricNumber(courseSummary, 'course.incompleteActionCount') || 0;
  const calculatedCompletionPercentage = plannedActionCount
    ? Math.round((completedActionCount * 100) / plannedActionCount)
    : null;
  return (
    <Space
      direction="vertical"
      size={16}
      className={styles.page}
      style={{ display: 'flex' }}
    >
      <Card bordered={false} className={styles.heroCard}>
        <Button
          icon={<ArrowLeftOutlined />}
          onClick={() => history.goBack()}
          style={{ marginBottom: 16 }}
        >
          返回会员训练档案
        </Button>
        <Title level={3} style={{ margin: 0 }}>
          {classroom?.title || '课程评估'}
        </Title>
        <Paragraph type="secondary" style={{ margin: '8px 0 0' }}>
          查看这节课的上课事实、课程汇总和每个动作的评估记录。
        </Paragraph>
      </Card>

      {!classroom ? (
        <Card>
          <Empty description="没有找到这节课" />
        </Card>
      ) : (
        <>
          <Card
            title="上课概览"
            bordered={false}
            className={styles.overviewCard}
          >
            <Descriptions column={{ xs: 1, sm: 2, lg: 4 }}>
              <Descriptions.Item label="课程名称">
                {classroom.title || '-'}
              </Descriptions.Item>
              <Descriptions.Item label="课程状态">
                <Tag color="green">{classroom.statusName || '已完课'}</Tag>
              </Descriptions.Item>
              <Descriptions.Item label="上课时间">
                {timeText(
                  classroom.actualStartAt || classroom.scheduledStartAt,
                )}
              </Descriptions.Item>
              <Descriptions.Item label="训练时长">
                {durationText(classroom.actualStartAt, classroom.actualEndAt)}
              </Descriptions.Item>
              <Descriptions.Item label="课程类型">
                {classroom.classTypeName || '-'}
              </Descriptions.Item>
              <Descriptions.Item label="训练计划">
                {classroom.planName || '-'}
              </Descriptions.Item>
            </Descriptions>
            <div className={styles.courseNumbers}>
              <div className={styles.completionMetric}>
                <Progress
                  type="circle"
                  width={72}
                  strokeWidth={9}
                  percent={calculatedCompletionPercentage || 0}
                  strokeColor="#20a57a"
                  trailColor="#e2ede9"
                />
                <span>动作完成</span>
              </div>
              <div>
                <strong>{plannedActionCount}</strong>
                <span>动作实例</span>
              </div>
              <div>
                <strong>{completedActionCount}</strong>
                <span>已完成</span>
              </div>
              <div>
                <strong>{skippedActionCount}</strong>
                <span>已跳过</span>
              </div>
              <div>
                <strong>{incompleteActionCount}</strong>
                <span>未完成</span>
              </div>
            </div>
          </Card>

          {detail.evaluationStatus === 'not_generated' ? (
            <Card>
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description="这节课已经完成，但课程评估尚未生成"
              />
            </Card>
          ) : !evaluation ? (
            <Alert
              type="warning"
              showIcon
              message="课程评估暂时不可用"
              description="上课记录仍可查看，稍后可再次进入本页查询评估。"
            />
          ) : (
            <>
              <Card
                bordered={false}
                className={styles.statusCard}
                title="评估进度"
              >
                <div className={styles.statusFlow}>
                  <div>
                    <Text type="secondary">客观记录</Text>
                    <Tag
                      color={
                        RECORD_STATUS_COLORS[evaluation.status] || 'default'
                      }
                    >
                      {RECORD_STATUS_LABELS[evaluation.status] || '状态已记录'}
                    </Tag>
                  </div>
                  <span className={styles.statusDivider} />
                  <div>
                    <Text type="secondary">智能结论</Text>
                    <Tag
                      color={
                        evaluation.assessmentStatus === 'succeeded'
                          ? 'green'
                          : evaluation.assessmentStatus === 'failed'
                          ? 'orange'
                          : 'blue'
                      }
                    >
                      {evaluation.assessmentStatus === 'succeeded'
                        ? '已生成'
                        : evaluation.assessmentStatus === 'failed'
                        ? '待重试'
                        : evaluation.assessmentStatus === 'generating'
                        ? '生成中'
                        : '等待生成'}
                    </Tag>
                  </div>
                  <Text type="secondary" className={styles.statusTime}>
                    客观记录生成于 {timeText(evaluation.ctime)}
                  </Text>
                </div>
              </Card>

              {evaluation.status === 'failed' && (
                <Alert
                  type="error"
                  showIcon
                  message="本次课程评估生成失败"
                  description={
                    evaluation.failureMessage || '暂时没有可展示的评估数据'
                  }
                />
              )}

              {evaluation.status === 'succeeded' &&
                evaluation.assessmentStatus === 'generating' && (
                  <Alert
                    type="info"
                    showIcon
                    message="课程判断生成中"
                    description="客观训练数据已经可以查看，系统正在形成本节课结论和下节关注点。"
                  />
                )}

              {evaluation.status === 'succeeded' &&
                (!evaluation.assessmentStatus ||
                  evaluation.assessmentStatus === 'not_generated') && (
                  <Alert
                    type="info"
                    showIcon
                    message="课程判断尚未生成"
                    description="本节课已有客观观察，但还没有形成结构化课程判断。"
                  />
                )}

              {evaluation.assessmentStatus === 'failed' && (
                <Alert
                  type="warning"
                  showIcon
                  message="智能课程结论生成失败，系统将自动重试；课程结构和完成数据不受影响"
                />
              )}

              {evaluation.assessmentStatus === 'succeeded' && !assessment && (
                <Alert
                  type="warning"
                  showIcon
                  message="课程判断内容无法读取"
                  description="系统已保留客观训练数据，请检查课程判断协议版本或重新生成。"
                />
              )}

              {evaluation.status === 'succeeded' && (
                <Card
                  title="本节课证据覆盖"
                  bordered={false}
                  className={styles.coverageCard}
                >
                  <Space wrap size={[8, 8]}>
                    <Tag
                      color={
                        availability.courseFactAvailable ? 'green' : 'default'
                      }
                    >
                      课程执行{' '}
                      {availability.courseFactAvailable ? '已读取' : '待补充'}
                    </Tag>
                    <Tag
                      color={
                        availability.actionMetadataCount > 0
                          ? 'green'
                          : 'default'
                      }
                    >
                      动作知识 {availability.actionMetadataCount || 0}/
                      {availability.distinctActionCount ||
                        availability.plannedActionCount ||
                        0}
                    </Tag>
                    <Tag
                      color={
                        availability.memberProfileAvailable
                          ? 'green'
                          : 'default'
                      }
                    >
                      会员课前档案{' '}
                      {availability.memberProfileAvailable
                        ? '已读取'
                        : '待补充'}
                    </Tag>
                    <Tag
                      color={
                        availability.preCourseCapabilityCount > 0
                          ? 'green'
                          : 'default'
                      }
                    >
                      课前能力 {availability.preCourseCapabilityCount || 0}{' '}
                      个维度
                    </Tag>
                    <Tag
                      color={
                        availability.motionObservationAvailable
                          ? 'blue'
                          : 'default'
                      }
                    >
                      动作质量{' '}
                      {availability.motionObservationAvailable
                        ? '已读取'
                        : '本课无数据'}
                    </Tag>
                  </Space>
                  {!availability.motionObservationAvailable && (
                    <Paragraph type="secondary" style={{ margin: '12px 0 0' }}>
                      本节课仍会依据完课事实、动作训练目的和会员课前情况形成事实级评估；
                      动作质量与稳定控制不作推断。
                    </Paragraph>
                  )}
                </Card>
              )}

              <CourseStructure
                actionInstances={actionInstances}
                actionNames={actionNames}
                onOpenAction={openActionDetail}
              />

              {evaluation.assessmentStatus === 'succeeded' && assessment && (
                <>
                  <Card
                    title="本节课结论"
                    bordered={false}
                    className={styles.conclusionCard}
                  >
                    <Space
                      direction="vertical"
                      size={12}
                      style={{ width: '100%' }}
                    >
                      <Space wrap>
                        {assessmentTag(
                          assessment.assessmentSummary?.overallStatus,
                        )}
                        <Tag>
                          {CONFIDENCE_NAMES[
                            assessment.assessmentSummary?.confidence
                          ] || '证据有限'}
                        </Tag>
                        <Text type="secondary">
                          生成时间：{timeText(evaluation.assessmentGeneratedAt)}
                        </Text>
                      </Space>
                      <Title level={4} style={{ margin: 0 }}>
                        {assessment.assessmentSummary?.headline ||
                          '本节课已形成结构化评估'}
                      </Title>
                    </Space>
                  </Card>

                  <Row gutter={[16, 16]}>
                    <Col xs={24} lg={12}>
                      <AssessmentSection
                        title="课前适配情况"
                        section={assessment.readiness}
                      />
                    </Col>
                    <Col xs={24} lg={12}>
                      <AssessmentSection
                        title="本课执行"
                        section={assessment.execution}
                      />
                    </Col>
                    {(assessment.dimensions || []).map((dimension) => (
                      <Col xs={24} lg={12} key={dimension.dimensionCode}>
                        <Card
                          size="small"
                          bordered={false}
                          className={styles.dimensionCard}
                          title={
                            DIMENSION_NAMES[dimension.dimensionCode] ||
                            dimension.dimensionCode
                          }
                          style={{ height: '100%' }}
                        >
                          <Space
                            direction="vertical"
                            size={8}
                            style={{ width: '100%' }}
                          >
                            {assessmentTag(dimension.status)}
                            <Text>{dimension.summary}</Text>
                            {(dimension.findings || []).map(
                              (finding, index) => (
                                <Alert
                                  key={`${dimension.dimensionCode}-${index}`}
                                  type={
                                    finding.type === 'priority_issue'
                                      ? 'warning'
                                      : finding.type === 'strength'
                                      ? 'success'
                                      : 'info'
                                  }
                                  showIcon
                                  message={finding.statement}
                                />
                              ),
                            )}
                          </Space>
                        </Card>
                      </Col>
                    ))}
                    <Col xs={24} lg={12}>
                      <AssessmentSection
                        title="与会员目标的匹配"
                        section={assessment.goalAlignment}
                      />
                    </Col>
                  </Row>

                  <Card
                    title="当前主要问题"
                    bordered={false}
                    className={styles.insightCard}
                  >
                    {assessment.primaryIssue ? (
                      <Alert
                        type="warning"
                        showIcon
                        message={assessment.primaryIssue.title}
                        description={assessment.primaryIssue.summary}
                      />
                    ) : (
                      <Empty
                        image={Empty.PRESENTED_IMAGE_SIMPLE}
                        description="当前证据不足以确认一个主要问题"
                      />
                    )}
                  </Card>

                  <Card
                    title="下节课重点关注"
                    bordered={false}
                    className={styles.insightCard}
                  >
                    {(assessment.nextSessionFocus || []).length ? (
                      <List
                        dataSource={assessment.nextSessionFocus}
                        renderItem={(item) => (
                          <List.Item>
                            <List.Item.Meta
                              title={item.title}
                              description={item.reason}
                            />
                          </List.Item>
                        )}
                      />
                    ) : (
                      <Empty
                        image={Empty.PRESENTED_IMAGE_SIMPLE}
                        description="当前证据不足以给出下节课重点"
                      />
                    )}
                  </Card>

                  {(assessment.limitations || []).length > 0 && (
                    <Collapse bordered={false} className={styles.rawEvidence}>
                      <Panel header="查看本次判断的数据边界" key="limitations">
                        <ul className={styles.limitations}>
                          {assessment.limitations.map((item) => (
                            <li key={item}>{item}</li>
                          ))}
                        </ul>
                      </Panel>
                    </Collapse>
                  )}
                </>
              )}
            </>
          )}
        </>
      )}
    </Space>
  );
}
